import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { getActiveHoliday, getHolidayRanges, HOLIDAY_LABELS } from "@/lib/holidays";

const Ctx = createContext({ settings: null, customEvents: [], active: null, reload: () => {} });

function inRangeStr(now, startStr, endStr) {
  if (!startStr || !endStr) return false;
  const s = new Date(startStr + "T00:00:00");
  s.setDate(s.getDate() - 1);
  const e = new Date(endStr + "T23:59:59");
  e.setDate(e.getDate() + 1);
  return now >= s && now <= e;
}

export function HolidayProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [customEvents, setCustomEvents] = useState([]);

  const reload = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/settings/holidays`),
        axios.get(`${API}/custom_events`),
      ]);
      setSettings(a.data);
      setCustomEvents(b.data || []);
    } catch { setSettings({}); setCustomEvents([]); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Vorschau-Overrides via URL (?preview_holiday=<key> | ?preview_custom=<id>)
  let previewHoliday = null, previewCustom = null;
  if (typeof window !== "undefined") {
    const p = new URLSearchParams(window.location.search);
    previewHoliday = p.get("preview_holiday");
    previewCustom = p.get("preview_custom");
  }

  // Aktives Event bestimmen (Priorität: Karfreitag > sonstige Feiertage > Custom).
  let active = null;
  if (previewHoliday) {
    const enabledMap = { ...(settings || {}) };
    Object.keys(HOLIDAY_LABELS).forEach(k => { enabledMap[k] = { enabled: k === previewHoliday, greeting: enabledMap[k]?.greeting }; });
    // Force ignore date range in preview.
    active = { kind: "holiday", key: previewHoliday, greeting: (settings?.[previewHoliday]?.greeting) || "", image_url: settings?.[previewHoliday]?.image_url };
  } else if (previewCustom) {
    const ev = customEvents.find(e => e.id === previewCustom);
    if (ev) active = { kind: "custom", id: ev.id, key: `custom-${ev.id}`, greeting: ev.greeting, name: ev.name, image_url: ev.image_url };
  } else if (settings) {
    const now = new Date();
    const enabledForActive = Object.fromEntries(Object.entries(settings).map(([k, v]) => [k, v?.enabled]));
    const key = getActiveHoliday(enabledForActive, now);
    if (key) active = { kind: "holiday", key, greeting: settings[key]?.greeting || "", image_url: settings[key]?.image_url };
    else {
      const ev = customEvents.find(e => e.is_active && inRangeStr(now, e.start_date, e.end_date));
      if (ev) active = { kind: "custom", id: ev.id, key: `custom-${ev.id}`, greeting: ev.greeting, name: ev.name, image_url: ev.image_url };
    }
  }

  return <Ctx.Provider value={{ settings, customEvents, active, previewOverride: previewHoliday || previewCustom, reload }}>{children}</Ctx.Provider>;
}

export const useHoliday = () => useContext(Ctx);

// Kontext für Custom-Event-Bilder: mappen wir mit image_url auf active.
